/* eslint-disable i18next/no-literal-string */
import React, { useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { StatusTypes } from 'types/graphql';
import useUser from 'hooks/use-user';
import { useRouter } from 'next/router';
import { tracker } from 'utils/analytics';
import MetaTags from 'components/meta-tags';
import { useListBankAccounts } from 'src/payments/hooks';
import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import { SignupLayout } from 'src/payments/layout/signup/layout';
import { useTranslation } from 'react-i18next';
import { createApolloClient } from 'utils/apollo/client';
import { gql } from '@apollo/client';
import { SuccessView } from 'src/payments/dutchie-pay/signup/views/success';
import useFeatureFlags from 'hooks/use-feature-flags';
import { AggregatorWidget } from 'src/payments/dutchie-pay/signup/views/aggregator-widget';
import { FullPageLoader } from 'shared/components';

import {
  AddressForm,
  EmailForm,
  PasswordForm,
  SignupForm,
  Loading,
  MXWidget,
  TermsView,
  BankAccountSelector,
  ResetPasswordForm,
  UnsupportedAccount,
} from './views';
import {
  EnrollmentSteps,
  tosSteps,
  connectingBankSteps,
  editBankAccount,
  CHECKOUT_PATH,
  relinkBankAccount,
} from '../../constants';

const {
  addressForm,
  connectBank,
  emailForm,
  forgotPasswordForm,
  passwordForm,
  signUpForm,
  terms,
  bankAccountSelector,
  bankAccountsLoading,
  tosLoading,
  unsupportedAccount,
  alreadySignedUp,
  success,
} = EnrollmentSteps;

export type ViewTextProps = {
  titleText?: string;
  subtitleText?: string;
};

const DutchiePaySignup = ({ queryStringStep, dispensary, plaidOath }): JSX.Element | null => {
  const editBankAccountEnabled = queryStringStep === editBankAccount;
  const relinkBankAccountEnabled = queryStringStep === relinkBankAccount;

  const User = useUser();
  const router = useRouter();
  const { t } = useTranslation();
  const FeatureFlags = useFeatureFlags();
  const featureFlagsLoading = FeatureFlags.loading;
  const { trackEnrollmentStepViewed } = useDutchiePayAnalytics();

  const userExists = useObserver(() => User.exists);

  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const { isPlaidEnabled } = DutchiePayEnrollment;

  const { setCurrentStep, userAlreadySignedUp } = DutchiePayEnrollment;
  const currentStep = useObserver(() => DutchiePayEnrollment.currentStep);
  const enrollmentStatus = useObserver(() => DutchiePayEnrollment.enrollmentStatus);
  const refUrl = useObserver(() => DutchiePayEnrollment.refUrl);
  const entryPoint = useObserver(() => DutchiePayEnrollment.entryPoint);
  const userCameFromCheckout = entryPoint === CHECKOUT_PATH;

  const { isEnrolledDutchiePay } = useDutchiePay();

  const { statusAggregatorError, mxWidgetUrl, billingAddressId } = useListBankAccounts({
    skip: false,
    relinkBankAccountEnabled,
  });

  const viewText = {
    email: {
      title: t('dutchiePay.email.title', 'Enter email to continue'),
      subTitle: t('dutchiePay.email.subtitle', "We'll check your email to login or sign up."),
    },
    signup: {
      title: t('dutchiePay.signup.title', 'Create your account'),
      subTitle: t('dutchiePay.signup.subtitle', 'Enter the info below to create your account.'),
    },
    address: {
      title: t('dutchiePay.address.title', 'Enter your home address'),
    },
  };

  const queryRefUrl = typeof router.query.refUrl === 'string' ? router.query.refUrl : '';

  const showSuccessScreen = currentStep === success || currentStep === alreadySignedUp;

  useEffect(() => {
    if (currentStep !== EnrollmentSteps.completeEnrollment) {
      trackEnrollmentStepViewed(currentStep);
    }
  }, [currentStep, trackEnrollmentStepViewed]);

  useEffect(() => {
    if (plaidOath) {
      DutchiePayEnrollment.plaidOauthReceived();
    } else if (!isEnrolledDutchiePay) {
      DutchiePayEnrollment.beginEnrollment(userExists, queryRefUrl || refUrl, isPlaidEnabled);
    }
    // remove age gate on the dutchiepay route
    User.setAgeOverride(99, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userExists]);

  useEffect(() => {
    DutchiePayEnrollment.setRefUrl(queryRefUrl);
    if (refUrl) {
      void router.replace({
        query: { ...router.query, refUrl },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryRefUrl, refUrl]);

  useEffect(() => {
    if (isEnrolledDutchiePay && (!editBankAccountEnabled || relinkBankAccountEnabled) && !refUrl) {
      userAlreadySignedUp();
    }
  }, [
    editBankAccountEnabled,
    isEnrolledDutchiePay,
    refUrl,
    setCurrentStep,
    relinkBankAccountEnabled,
    userAlreadySignedUp,
  ]);

  useEffect(() => {
    if (statusAggregatorError) {
      setCurrentStep(connectBank);
    }
  }, [setCurrentStep, statusAggregatorError]);

  useEffect(() => {
    if (relinkBankAccountEnabled) {
      DutchiePayEnrollment.reconnectBankAccount({
        mxWidgetUrl,
        billingAddressId,
        relinkBankAccountEnabled,
        isPlaidEnabled,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relinkBankAccountEnabled, mxWidgetUrl, billingAddressId]);

  useEffect(() => {
    tracker.setContext({ activeIsUserEnrolledDutchiePay: enrollmentStatus === StatusTypes.statusEnrolled });

    if (currentStep === EnrollmentSteps.completeEnrollment) {
      void router.push(refUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, enrollmentStatus]); // FIXME: ENG-32714 fix hooks dependency

  useEffect(() => {
    if (dispensary) {
      DutchiePayEnrollment.coBrandedSignup = true;
      DutchiePayEnrollment.setDispensary(dispensary);
    }
    if (refUrl.includes(CHECKOUT_PATH)) {
      DutchiePayEnrollment.entryPoint = CHECKOUT_PATH;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispensary, refUrl]);

  if (featureFlagsLoading || (plaidOath && currentStep === emailForm)) {
    return <FullPageLoader />;
  }

  const BankLinkWidget = !isPlaidEnabled ? MXWidget : AggregatorWidget;
  return (
    <>
      <MetaTags />
      <SignupLayout>
        {/* Forms */}
        {currentStep === emailForm && (
          <EmailForm titleText={viewText.email.title} subtitleText={viewText.email.subTitle} />
        )}
        {currentStep === passwordForm && <PasswordForm showToolTip={userCameFromCheckout} />}
        {currentStep === forgotPasswordForm && <ResetPasswordForm />}
        {currentStep === signUpForm && (
          <SignupForm titleText={viewText.signup.title} subtitleText={viewText.signup.subTitle} />
        )}
        {currentStep === addressForm && <AddressForm titleText={viewText.address.title} />}

        {currentStep === connectBank && <BankLinkWidget relinkBankAccountEnabled={relinkBankAccountEnabled} />}

        {currentStep === bankAccountSelector && <BankAccountSelector />}

        {currentStep === terms && <TermsView />}

        {currentStep === unsupportedAccount && <UnsupportedAccount />}
        {showSuccessScreen && <SuccessView />}
        {/* loaders */}
        {currentStep === bankAccountsLoading && <Loading steps={connectingBankSteps} />}
        {currentStep === tosLoading && <Loading steps={tosSteps} />}
      </SignupLayout>
    </>
  );
};
export default DutchiePaySignup;

DutchiePaySignup.getInitialProps = async ({ query }) => {
  const client = createApolloClient();
  const gqlQuery = gql`
    query filteredDispensaries($dispensaryId: String!) {
      filteredDispensaries(filter: { cNameOrID: $dispensaryId }) {
        SpecialLogoImage
        name
      }
    }
  `;
  // this needs to stay as snake case as this is set from plaid
  // eslint-disable-next-line @typescript-eslint/naming-convention,camelcase
  const { step, refUrl, cname, oauth_state_id } = query;

  let result;
  if (cname) {
    result = await client.query({
      query: gqlQuery,
      variables: {
        dispensaryId: cname,
      },
    });
  }

  return {
    queryStringStep: step,
    refUrl,
    dispensary: result?.data?.filteredDispensaries[0],
    plaidOath: oauth_state_id,
  };
};
